import { getHandymanPriceList } from '../api/handyman/handyman-service-api'
import { getRequestJSON } from '../api/index/location-api'
import priceSnapshot from '../data/handyman-price-snapshot.json'
import categorySettingsSnapshot from '../data/handyman-category-settings-snapshot.json'

// Build-time loaders for the handyman / electrician / plumber pages.
//
// These pages revalidate every 3 days. Before this existed, getStaticProps
// awaited the price and category APIs with no guard, so one failed call either
// failed the build or left ISR serving a stale page with no way to recover
// until the next successful regeneration. Both loaders now fall back to a
// committed snapshot of the same public endpoint, and log when they do.
//
// Refresh the snapshots whenever prices change:
//   GET {API}/public/GetHandymanPriceList?country=Singapore
//   GET {API}/public/GetServiceCategorySettings?countryName=Singapore  (Handyman Services entry only)
//
// Prices live in the API. Nothing in this file should hard-code an amount.

// The API returns float noise such as 103.96000000000001.
const round2 = (n) => Math.round(Number(n) * 100) / 100

const normalise = (list) =>
    list
        .filter((item) => item && item.itemName && Number.isFinite(Number(item.serviceCost)))
        .map((item) => ({ ...item, serviceCost: round2(item.serviceCost) }))

export const loadHandymanPriceList = async (country = 'Singapore') => {
    try {
        const list = await getHandymanPriceList(country)
        if (Array.isArray(list) && list.length > 0) return normalise(list)
        console.warn('[handyman-price-data] price API returned no items, using committed snapshot')
    } catch (error) {
        console.warn('[handyman-price-data] price API failed, using committed snapshot:', error?.message)
    }
    return normalise(priceSnapshot)
}

// Services.js reads requestCategorySettings[0].businessType and walks
// criteriaQuestions to build the booking wizard, so the fallback has to be the
// full settings object - an empty array would crash the booking modal.
export const loadHandymanCategorySettings = async (countryName = 'Singapore') => {
    try {
        const all = await getRequestJSON(countryName)
        const settings = Array.isArray(all)
            ? all.filter((category) => category.requestType === 'Handyman Services')
            : []
        if (settings.length > 0) return settings
        console.warn('[handyman-price-data] no Handyman Services category settings, using committed snapshot')
    } catch (error) {
        console.warn('[handyman-price-data] category settings API failed, using committed snapshot:', error?.message)
    }
    return categorySettingsSnapshot
}

export const filterByTechnician = (list, technicianType) =>
    (list || []).filter((item) => item.technicianType === technicianType)

// Exact itemName matches, in the order the names were given. Unknown names
// are reported so a renamed API item shows up in the build log rather than
// silently disappearing from a page.
export const findItems = (list, itemNames, context = '') => {
    const found = []
    for (const name of itemNames || []) {
        const match = (list || []).find((item) => item.itemName === name)
        if (match) {
            found.push(match)
        } else {
            console.warn(`[handyman-price-data] ${context ? context + ': ' : ''}no price item named "${name}"`)
        }
    }
    return found
}

export const priceRange = (items) => {
    const costs = (items || []).map((item) => item.serviceCost).filter(Number.isFinite)
    if (costs.length === 0) return null
    return { low: Math.min(...costs), high: Math.max(...costs) }
}

export const fromPrice = (items) => priceRange(items)?.low ?? null

// "$67" for whole-dollar headlines (titles, meta), "$67.16" for price tables.
export const formatWhole = (n) => `$${Math.floor(n)}`
export const formatPrice = (n) => `$${Number(n).toFixed(2)}`

// "S$67.16 - S$324.76", the format buildServiceNode() parses.
export const priceRangeLabel = (items) => {
    const range = priceRange(items)
    return range ? `S$${range.low.toFixed(2)} - S$${range.high.toFixed(2)}` : undefined
}

// Price range per category, in display order, for the summary cards.
export const CATEGORY_ORDER = [
    'Electrical', 'Plumbing', 'Doors, Cabinets & Furniture', 'Drilling & Installation', 'Painting',
    // Pre-2026 category names, until the new price list reaches production.
    'Kitchen', 'Bathroom', 'Door/Cabinet', 'Drilling/Installation', 'Bedroom',
]

export const groupByCategory = (items) => {
    const groups = (items || []).reduce((acc, item) => {
        const key = item.category || 'Other'
        if (!acc[key]) acc[key] = []
        acc[key].push(item)
        return acc
    }, {})
    // Known categories first, in the fixed order; anything new the API adds
    // is appended rather than dropped.
    const known = CATEGORY_ORDER.filter((cat) => groups[cat])
    const unknown = Object.keys(groups).filter((cat) => !CATEGORY_ORDER.includes(cat)).sort()
    return [...known, ...unknown].map((category) => ({ category, items: groups[category] }))
}

export const categorySlug = (category) =>
    String(category).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

/** Schema.org OfferCatalog generated from the live list, so it cannot drift. */
export const buildOfferCatalog = (items, name) => ({
    '@type': 'OfferCatalog',
    name,
    itemListElement: (items || []).map((item) => ({
        '@type': 'Offer',
        name: item.itemName,
        priceCurrency: 'SGD',
        price: item.serviceCost.toFixed(2),
        itemOffered: { '@type': 'Service', name: item.itemName },
    })),
})
